import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { ChatByIdView } from "@/Chat/ChatSidebar/ChatByIdView";
import { useDomainServices } from "@/providers/DomainServices";
import { Button } from "@/shared/foundation/primitives";

import styles from "./AdminChatViewer.module.css";
import { ReadOnlyAIChatProvider } from "./ReadOnlyAIChatProvider";

interface AdminChatViewerProps {
  chatId: string;
  onBack: () => void;
}

export function AdminChatViewer({ chatId, onBack }: AdminChatViewerProps) {
  const { chatService } = useDomainServices();
  const {
    data: chat,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["adminChat", chatId],
    queryFn: ({ signal }) =>
      chatService.getChatById(chatId, true, { abortSignal: signal }),
    enabled: !!chatId,
  });
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Scroll the viewer into view when a chat is selected
  React.useEffect(() => {
    containerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [chatId]);

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.header}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>
        {chat && (
          <div className={styles.metadata}>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Org:</span>
              <span className={styles.metadataValue}>{chat.org_id}</span>
            </div>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>User:</span>
              <span className={styles.metadataValue}>{chat.created_by}</span>
            </div>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Created:</span>
              <span className={styles.metadataValue}>
                {new Date(chat.created).toLocaleString()}
              </span>
            </div>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Model:</span>
              <span className={styles.metadataValue}>
                {chat.model_profile || "default"}
              </span>
            </div>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Messages:</span>
              <span className={styles.metadataValue}>
                {chat.messages.length}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className={styles.chatContent}>
        {isLoading && (
          <div className={styles.loadingContainer}>
            <CircularProgress size="1.5rem" />
          </div>
        )}
        {error && (
          <div className={styles.errorContainer}>
            Failed to load chat: {error.message}
          </div>
        )}
        {chat && (
          <ReadOnlyAIChatProvider>
            <ChatByIdView chat={chat} />
          </ReadOnlyAIChatProvider>
        )}
      </div>

      <div className={styles.readOnlyBanner}>Read-only view</div>
    </div>
  );
}
